import { useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { PausedAccountScreen } from "@/components/PausedAccountScreen";
import { PaymentFailedScreen } from "@/components/PaymentFailedScreen";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Routes any signed-in user can reach regardless of subscription state.
// Messages stays open so former students can read and answer EduFocal Bot outreach.
const SUBSCRIPTION_EXEMPT_PATHS = ["/messages"];

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const [location, setLocation] = useLocation();

  const isActuallyAuthenticated = !!user;
  const isSubscriptionExempt = SUBSCRIPTION_EXEMPT_PATHS.some((path) => location.startsWith(path));

  useEffect(() => {
    if (isLoading) return;

    if (!isActuallyAuthenticated) {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      setLocation('/login?returnTo=' + returnTo);
      return;
    }

    if (user.role === 'admin') return;

    if (isSubscriptionExempt) return;

    // Paused accounts stay on the paused screen, not the subscribe page
    if (user.subscriptionStatus === 'paused') return;

    // past_due accounts stay in the app (grace period or payment failed screen)
    if (user.subscriptionStatus === 'past_due') return;

    const validStatuses = ['active', 'trial', 'pending'];
    const hasValidStatus = user.subscriptionStatus && validStatuses.includes(user.subscriptionStatus);

    if (!hasValidStatus) {
      setLocation('/subscribe');
    }
  }, [user, isLoading, isActuallyAuthenticated, isSubscriptionExempt, setLocation]);

  if (isLoading) return null;
  if (!isActuallyAuthenticated) return null;

  if (user.role !== 'admin' && !isSubscriptionExempt) {
    // Show the paused screen (subscription voluntarily paused)
    if (user.subscriptionStatus === 'paused') {
      return <PausedAccountScreen resumesAt={(user as any).subscriptionPausedUntil ?? null} />;
    }

    // Payment failed: check if within 3-day grace period
    if (user.subscriptionStatus === 'past_due') {
      const failedAt = (user as any).paymentFailedAt ? new Date((user as any).paymentFailedAt) : null;
      const daysSinceFailure = failedAt
        ? Math.floor((Date.now() - failedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 999;

      if (daysSinceFailure >= 3) {
        // Grace period over: show payment failed screen (blocks access)
        return <PaymentFailedScreen user={user} />;
      }
      // Within grace period: show app with banner (banner rendered in children layout)
    }

    const validStatuses = ['active', 'trial', 'pending', 'past_due'];
    const hasValidStatus = user.subscriptionStatus && validStatuses.includes(user.subscriptionStatus);

    if (!hasValidStatus) return null;
  }

  return <>{children}</>;
}
